import { inject, Injectable } from '@angular/core';

import '@mediapipe/face_detection';
import '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import '@tensorflow/tfjs-backend-cpu';
import * as tf from '@tensorflow/tfjs-core';
import * as faceDetection from '@tensorflow-models/face-detection';

import { FaceTracker } from './face-tracker';
import { FaceTrackingData } from '@models/face-tracking-data';
import { SystemCheck } from './system-check';

export class MediaPipeTrackingData extends FaceTrackingData {
  constructor(public faces: faceDetection.Face[]) {
    super();
  }

  get type(): string {
    return 'MediaPipeDatum';
  }
}

type Backend = 'webgl' | 'cpu';

@Injectable({ providedIn: 'root' })
export class MediaPipeTrackerService extends FaceTracker {
  detector: faceDetection.FaceDetector;

  private backend: Backend;
  private systemCheck = inject(SystemCheck);

  async initialize(): Promise<this> {
    // Check if WebGL context can be created before attempting to use it
    this.backend = this.systemCheck.hasGraphicsAcceleration ? 'webgl' : 'cpu';

    return this.initializeWithBackend();
  }

  protected async trackFaces() {
    const canvas = await this.stream.camera.snapCanvas(this.stream);
    const faces = await this.detector.estimateFaces(canvas);

    this.faceTrackingData.emit(new MediaPipeTrackingData(faces));
  }

  private async initializeWithBackend(): Promise<this> {
    try {
      await tf.setBackend(this.backend);
      await tf.ready();

      console.log(`Initializing TensorFlow.js with backend ${this.backend}`);
    } catch (backendError) {
      // If WebGL backend failed, try CPU before exploding.
      if (this.backend === 'webgl') {
        console.warn('TensorFlow.js backend fallback: WebGL → CPU', backendError);

        this.backend = 'cpu';
        return this.initializeWithBackend();
      }

      throw new Error(`Failed to initialize TensorFlow.js backend for face detection: ${backendError}`);
    }

    const model = faceDetection.SupportedModels.MediaPipeFaceDetector;
    const detectorConfig = this.buildDetectorConfig();
    this.detector = await faceDetection.createDetector(model, detectorConfig);

    return this;
  }

  // Use tfjs runtime for CPU backend, mediapipe runtime for WebGL backend
  private buildDetectorConfig():
    | faceDetection.MediaPipeFaceDetectorTfjsModelConfig
    | faceDetection.MediaPipeFaceDetectorMediaPipeModelConfig {
    if (this.backend === 'cpu') {
      return {
        runtime: 'tfjs',
        modelType: 'short'
      };
    }

    return {
      runtime: 'mediapipe',
      solutionPath: 'mediapipe/face_detection'
    };
  }
}
